import { useState, useRef, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { AlertCircle, BookOpen, FileText, Sparkles, Settings, Archive, Map, Database } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useScenarioStore } from '../../../../stores/scenarioStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import { useNameStore } from '../../../../stores/nameStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useTitleStore } from '../../../../stores/titleStore'
import { useWikipediaStore } from '../../../../stores/wikipediaStore'
import { useImagePromptStore } from '../../../../stores/imagePromptStore'
import { useDocumentStore } from '../../../../stores/documentStore'
import type { 
  ScenarioInput, 
  ScenarioResult, 
  SystemAnalysisResult, 
  FormatAnalysisResult,
  SavedScenario,
  SavedSystem,
  SavedFormat
} from '@creanizer/types'
// import { ScenarioGenerateMode } from './ScenarioGenerateMode'
import { ScenarioGenerateModeSimple } from './ScenarioGenerateModeSimple'
import { SystemAnalysisMode } from './SystemAnalysisMode'
import { FormatAnalysisMode } from './FormatAnalysisMode'
import { ScenarioResult as ScenarioResultDisplay } from './ScenarioResultDynamic'
import { SystemAnalysisResult as SystemResultDisplay } from './SystemAnalysisResult'
import { FormatAnalysisResult as FormatResultDisplay } from './FormatAnalysisResult'
import { SavedScenariosList } from './SavedScenariosList'
import { SavedSystemsList } from './SavedSystemsList'
import { SavedFormatsList } from './SavedFormatsList'
import { ImportExportSection } from './ImportExportSection'
import { SkillDatabaseTab } from './SkillDatabaseTab'

export function ScenarioGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { 
    scenarios = [], 
    systems = [], 
    formats = [], 
    addScenario, 
    addSystem, 
    addFormat,
    getSystem,
    getFormat,
    incrementFormatUsage
  } = useScenarioStore()
  
  const generator = useGenerator('scenario')
  const streamGenerator = useStreamGenerator('scenario')
  
  const [activeTab, setActiveTab] = useState('generate')
  const [mode, setMode] = useState<'generate' | 'analyze_system' | 'analyze_format'>('generate')
  const [useStreaming, setUseStreaming] = useState(false) // ストリーミングはデフォルトOFF
  
  // 入力状態
  const [input, setInput] = useState<ScenarioInput>({
    mode: 'generate',
    generateSettings: {
      genre: 'fantasy',
      difficulty: 'intermediate',
      playTime: '2-3h',
      playerCount: '3-5人',
      creativityLevel: 7,
      includeGMNotes: true,
      includeMaps: false,
      includeHandouts: false,
      includeTimeline: false,
      useProMode: false
    },
    systemAnalysisSettings: undefined,
    formatAnalysisSettings: undefined
  })
  
  // モード切り替え時の処理
  const handleModeChange = (newMode: 'generate' | 'analyze_system' | 'analyze_format') => {
    setMode(newMode)
    setResult(null)
    setError('')
    // 編集モードをクリア
    setEditingSystemId(null)
    setEditingFormatId(null)
    
    // モードに応じて入力状態をリセット
    if (newMode === 'generate') {
      setInput({
        mode: 'generate',
        generateSettings: {
          genre: 'fantasy',
          difficulty: 'intermediate',
          playTime: '2-3h',
          playerCount: '3-5人',
          creativityLevel: 7,
          includeGMNotes: true,
          includeMaps: false,
          includeHandouts: false,
          includeTimeline: false,
          useProMode: false
        },
        systemAnalysisSettings: undefined,
        formatAnalysisSettings: undefined
      })
    } else if (newMode === 'analyze_system') {
      setInput({
        mode: 'analyze_system',
        generateSettings: undefined,
        systemAnalysisSettings: {
          sourceType: 'description',
          sourceText: '',
          includeExamples: true
        },
        formatAnalysisSettings: undefined
      })
    } else if (newMode === 'analyze_format') {
      setInput({
        mode: 'analyze_format',
        generateSettings: undefined,
        systemAnalysisSettings: undefined,
        formatAnalysisSettings: {
          sourceType: 'scenario',
          sourceText: '',
          extractStructure: true,
          extractStyle: true,
          extractPatterns: true
        }
      })
    }
  }
  
  // 結果状態
  const [result, setResult] = useState<ScenarioResult | SystemAnalysisResult | FormatAnalysisResult | null>(null)
  const [error, setError] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [editingSystemId, setEditingSystemId] = useState<string | null>(null)
  const [editingFormatId, setEditingFormatId] = useState<string | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  
  // モード切り替え時の処理
  useEffect(() => {
    // モード切り替え時は入力を完全にリセット
    setInput(prev => ({
      mode,
      generateSettings: mode === 'generate' ? {
        genre: 'fantasy',
        difficulty: 'intermediate',
        playTime: '2-3h',
        playerCount: '3-5人',
        creativityLevel: 7,
        includeGMNotes: true,
        includeMaps: false,
        includeHandouts: false,
        includeTimeline: false,
        useProMode: false
      } : undefined,
      systemAnalysisSettings: mode === 'analyze_system' ? {
        sourceType: 'rulebook',
        sourceText: '',
        includeExamples: true
      } : undefined,
      formatAnalysisSettings: mode === 'analyze_format' ? {
        sourceType: 'scenario',
        sourceText: '',
        extractStructure: true,
        extractStyle: true,
        extractPatterns: true
      } : undefined
    }))
    setResult(null)
    setError('')
  }, [mode])
  
  // 生成処理
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResult(null)
    setIsGenerating(true)
    
    try {
      // 送信用のinputを準備
      let requestInput = { ...input }
      
      // システムとフォーマットの読み込み
      if (mode === 'generate' && input.generateSettings) {
        const { systemId, formatId } = input.generateSettings
        
        // システムの完全データを読み込み
        if (systemId && systemId !== 'none' && systemId !== 'custom') {
          const system = getSystem(systemId)
          if (system) {
            // システムの完全データをinputに含める
            console.log('Using system:', system.name)
            requestInput.generateSettings = {
              ...requestInput.generateSettings!,
              systemData: system.result
            }
          }
        }
        
        // フォーマットの完全データを読み込み
        if (formatId && formatId !== 'none' && formatId !== 'custom') {
          const format = getFormat(formatId)
          if (format) {
            // 使用回数をインクリメント
            incrementFormatUsage(formatId)
            // フォーマットの完全データをinputに含める
            console.log('Using format:', format.name)
            requestInput.generateSettings = {
              ...requestInput.generateSettings!,
              formatData: format.result
            }
          }
        }
      }
      
      // ストリーミング or 通常生成
      if (useStreaming && mode === 'generate') {
        await new Promise<void>((resolve, reject) => {
          streamGenerator.generateStream(requestInput, {
            onChunk: (chunk) => {
              console.log('Received chunk:', chunk.length)
            },
            onProgress: (bytes) => {
              console.log('Progress:', bytes)
            },
            onComplete: (resultData) => {
              if (resultData) {
                setResult(resultData as ScenarioResult)
                // 結果を保存
                handleSaveResult(resultData)
                resolve()
              } else {
                reject(new Error('生成に失敗しました'))
              }
            },
            onError: (error) => {
              console.error('Stream error:', error)
              reject(error)
            }
          })
        })
      } else {
        const response = await generator.mutateAsync(requestInput)
        const resultData = response.result || response
        
        if (resultData) {
          const typedResult = resultData as ScenarioResult | SystemAnalysisResult | FormatAnalysisResult
          setResult(typedResult)
          // 結果を保存
          handleSaveResult(typedResult)
        }
      }
      
      // 結果表示エリアにスクロール
      if (resultRef.current) {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (err: any) {
      console.error('Generation error:', err)
      setError(err.message || '生成中にエラーが発生しました')
    } finally {
      setIsGenerating(false)
    }
  }
  
  // 結果の保存
  // 保存済みシステムを編集モードで開く
  const handleEditSystem = (systemId: string) => {
    const system = getSystem(systemId)
    if (system) {
      // 編集モードフラグを先に設定
      setEditingSystemId(systemId)
      // モード変更
      setMode('analyze_system')
      // 少し遅延させてresultとinputを設定（useEffectのリセット後に実行）
      setTimeout(() => {
        setResult(system.result)
        setInput({
          mode: 'analyze_system',
          systemAnalysisSettings: system.input.systemAnalysisSettings
        })
      }, 0)
      setActiveTab('generate')
      // 結果エリアにスクロール
      setTimeout(() => {
        if (resultRef.current) {
          resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }
  
  // 保存済みフォーマットを編集モードで開く
  const handleEditFormat = (formatId: string) => {
    const format = getFormat(formatId)
    if (format) {
      // 編集モードフラグを先に設定
      setEditingFormatId(formatId)
      // モード変更
      setMode('analyze_format')
      // 少し遅延させてresultとinputを設定（useEffectのリセット後に実行）
      setTimeout(() => {
        setResult(format.result)
        setInput({
          mode: 'analyze_format',
          formatAnalysisSettings: format.input.formatAnalysisSettings
        })
      }, 0)
      setActiveTab('generate')
      // 結果エリアにスクロール
      setTimeout(() => {
        if (resultRef.current) {
          resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }
  
  const handleSaveResult = (resultData: ScenarioResult | SystemAnalysisResult | FormatAnalysisResult) => {
    if (mode === 'generate') {
      const scenarioResult = resultData as ScenarioResult
      addScenario({
        title: scenarioResult.basicInfo.title,
        input,
        result: scenarioResult,
        systemId: input.generateSettings?.systemId,
        formatId: input.generateSettings?.formatId,
        worldId: undefined, // TODO: 世界観連携時に設定
        tags: scenarioResult.basicInfo.tags,
        category: scenarioResult.basicInfo.genre
      })
    } else if (mode === 'analyze_system') {
      const systemResult = resultData as SystemAnalysisResult
      addSystem({
        name: systemResult.systemInfo?.name || '無題のシステム',
        input,
        result: systemResult,
        category: systemResult.systemInfo?.category || 'unknown',
        tags: []
      })
    } else if (mode === 'analyze_format') {
      const formatResult = resultData as FormatAnalysisResult
      addFormat({
        name: formatResult.formatInfo?.name || '無題のフォーマット',
        input,
        result: formatResult,
        category: formatResult.formatInfo?.style || 'unknown',
        tags: []
      })
    }
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-purple-500" />
          シナリオジェネレータ
        </h1>
        <p className="text-gray-600">
          TRPGやゲームのシナリオを生成・解析します。システムやフォーマットを保存して再利用できます。
        </p>
      </div>
      
      {/* モード選択 */}
      <div className="card p-6">
        <Label htmlFor="mode-select">動作モード</Label>
        <Select value={mode} onValueChange={(value) => handleModeChange(value as typeof mode)}>
          <SelectTrigger id="mode-select" className="mt-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="generate">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                シナリオ生成
              </div>
            </SelectItem>
            <SelectItem value="analyze_system">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                システム解析
              </div>
            </SelectItem>
            <SelectItem value="analyze_format">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                フォーマット解析
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          {mode === 'generate' && (
            <p className="text-sm text-gray-600">
              保存済みのシステムとフォーマットを使用して、シナリオを生成します。
              他のジェネレータで作成した要素も組み込めます。
            </p>
          )}
          {mode === 'analyze_system' && (
            <p className="text-sm text-gray-600">
              TRPGのルールブックやプレイログからシステムルールを解析し、保存します。
              保存したシステムは生成時に利用できます。
            </p>
          )}
          {mode === 'analyze_format' && (
            <p className="text-sm text-gray-600">
              既存のシナリオからフォーマット構造を解析し、保存します。
              保存したフォーマットは生成時に利用できます。
            </p>
          )}
        </div>
      </div>
      
      {/* メインタブ */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="generate">
            <Sparkles className="w-4 h-4 mr-2" />
            生成
          </TabsTrigger>
          <TabsTrigger value="saved">
            <Archive className="w-4 h-4 mr-2" />
            保存済み
          </TabsTrigger>
          <TabsTrigger value="systems">
            <Settings className="w-4 h-4 mr-2" />
            システム
          </TabsTrigger>
          <TabsTrigger value="formats">
            <FileText className="w-4 h-4 mr-2" />
            フォーマット
          </TabsTrigger>
          <TabsTrigger value="skill-db">
            <Database className="w-4 h-4 mr-2" />
            スキルDB
          </TabsTrigger>
          <TabsTrigger value="import-export">
            <Map className="w-4 h-4 mr-2" />
            入出力
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-6">
          {/* ストリーミングオプション */}
          {mode === 'generate' && (
            <div className="card p-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="use-streaming"
                  checked={useStreaming}
                  onCheckedChange={(checked) => setUseStreaming(checked === true)}
                />
                <Label htmlFor="use-streaming">
                  ストリーミング生成を使用（リアルタイムで結果を表示）
                </Label>
              </div>
            </div>
          )}
          
          {/* モード別の入力フォーム */}
          {mode === 'generate' && (
            <ScenarioGenerateModeSimple
              input={input}
              setInput={setInput}
              systems={systems}
              formats={formats}
            />
          )}
          {mode === 'analyze_system' && (
            <SystemAnalysisMode
              input={input}
              setInput={setInput}
            />
          )}
          {mode === 'analyze_format' && (
            <FormatAnalysisMode
              input={input}
              setInput={setInput}
            />
          )}
          
          {/* エラー表示 */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* 生成ボタン */}
          <div className="flex justify-center">
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !isApiKeySet()}
              size="lg"
              className="min-w-[200px]"
            >
              {isGenerating ? (
                <>生成中...</>
              ) : mode === 'generate' ? (
                <>シナリオを生成</>
              ) : mode === 'analyze_system' ? (
                <>システムを解析</>
              ) : (
                <>フォーマットを解析</>
              )}
            </Button>
          </div>
          
          {/* 結果表示 */}
          {result && (
            <div ref={resultRef} className="space-y-6">
              {mode === 'generate' && (
                <ScenarioResultDisplay 
                  result={result as ScenarioResult} 
                  originalPrompt={input}
                  onResultUpdate={(newResult) => setResult(newResult)}
                />
              )}
              {mode === 'analyze_system' && (
                <SystemResultDisplay 
                  result={result as SystemAnalysisResult} 
                  originalInput={input.systemAnalysisSettings || {}}
                  onResultUpdate={(newResult) => setResult(newResult)}
                />
              )}
              {mode === 'analyze_format' && (
                <FormatResultDisplay 
                  result={result as FormatAnalysisResult} 
                  originalInput={input.formatAnalysisSettings || {}}
                  onResultUpdate={(newResult) => setResult(newResult)}
                />
              )}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="saved">
          <SavedScenariosList />
        </TabsContent>
        
        <TabsContent value="systems">
          <SavedSystemsList onEdit={handleEditSystem} />
        </TabsContent>
        
        <TabsContent value="formats">
          <SavedFormatsList onEdit={handleEditFormat} />
        </TabsContent>
        
        <TabsContent value="skill-db">
          <SkillDatabaseTab />
        </TabsContent>

        <TabsContent value="import-export">
          <ImportExportSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}